import { Children, cloneElement } from 'react'
import { RadioGroup as MuiRadioGroup, type RadioGroupProps as MuiRadioGroupProps } from '@mui/material'
import { type PropsWithSpecificChildren, type HTMLAttributesWithoutStyling, type Option } from '../../types'
import { Radio, type RadioProps } from '../Radio'

export type RadioGroupProps<OptionValue> = Omit<HTMLAttributesWithoutStyling<HTMLInputElement>, 'defaultValue'> &
  Pick<MuiRadioGroupProps, 'name' | 'row' | 'onChange'> & {
    value?: OptionValue
    defaultValue?: OptionValue
    disabled?: boolean
  } & (
    | {
        children: PropsWithSpecificChildren<RadioProps>
        options?: never
      }
    | {
        options: Option<OptionValue>[]
        children?: never
      }
  )

/**
 * RadioGroup
 *
 * Demos:
 * - [RadioGroup](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/inputs-radiogroup--docs)
 *
 */

export const RadioGroup = <OptionValue,>({
  row,
  options,
  name,
  children,
  disabled,
  ...props
}: RadioGroupProps<OptionValue>) => (
  <MuiRadioGroup {...props} row={row} name={name}>
    {options
      ? options.map((option) => <Radio key={String(option.value)} disabled={disabled} {...option} />)
      : /**
         * assign the disabled prop to each child
         */
        Children.map(children, (child) => cloneElement(child, { disabled }))}
  </MuiRadioGroup>
)
